import {useContext} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import IconHeartOutline from '@adv-mt/mt-svg-icons/lib/HeartOutline.js'
import IconHeartSolid from '@adv-mt/mt-svg-icons/lib/HeartSolid.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'

const DEFAULT_SELLER_TYPE = 'profesional'
const PRODUCT_CATEGORY_DEALER = 'dealer'

const FavoriteDealerButton = ({contractId, isFavorite = false, trackingData = {}}) => {
  const {domain, i18n} = useSuiContext()
  const {triggerLoginModal} = useContext(LoginModalContext)
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {LOGIN_SOURCE_ID_DEALER_FAV} = config.get('login')
  const {AUTHENTICATION_HIT_INFORMATIONS_2, HIT_ORIGINS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const trackFavorite = nextIsFavorite =>
    track({
      event: nextIsFavorite ? TRACKING_EVENT_NAMES.DEALER_FAVORITED : TRACKING_EVENT_NAMES.DEALER_UNFAVORITED,
      data: {
        ...trackingData,
        hit_origin: HIT_ORIGINS.AD_DETAIL,
        seller_type: trackingData.seller_type || DEFAULT_SELLER_TYPE,
        products: [{category: PRODUCT_CATEGORY_DEALER, id: `${PRODUCT_CATEGORY_DEALER}${contractId}`}]
      }
    })

  const addFavoriteAfterLogin = async () => {
    const [error] = await domain.get('add_user_favorites_dealer_use_case').execute({contractId})

    if (!error) trackFavorite(true)
  }

  const handleClick = async () => {
    const useCaseId = isFavorite ? 'delete_user_favorites_dealer_use_case' : 'add_user_favorites_dealer_use_case'

    const [error] = await domain.get(useCaseId).execute({contractId})

    if (error instanceof USER_NOT_LOGGED_IN_USER_ERROR) {
      triggerLoginModal({
        modalProps: {
          hitInformation2: AUTHENTICATION_HIT_INFORMATIONS_2.DETAIL,
          onLogin: addFavoriteAfterLogin,
          source: LOGIN_SOURCE_ID_DEALER_FAV,
          trackingOrigin: HIT_ORIGINS.DEALER_FAVORITED
        }
      })
      return
    }

    if (!error) trackFavorite(!isFavorite)
  }

  return (
    <Button
      aria-label={i18n.t(isFavorite ? 'COMMON.REMOVE_FAVORITE_DEALER' : 'COMMON.ADD_FAVORITE_DEALER')}
      aria-pressed={isFavorite}
      onClick={handleClick}
      shape={BUTTON_SHAPES.CIRCLE}
      size={BUTTON_SIZES.MEDIUM}
      variant={BUTTON_VARIANTS.FLAT}
    >
      <Icon intent={ICON_INTENTS.ACCENT} size={ICON_SIZES.MD}>
        {isFavorite ? (
          <IconHeartSolid size={ICON_SIZES.MD} color={ICON_INTENTS.ACCENT} />
        ) : (
          <IconHeartOutline size={ICON_SIZES.MD} color={ICON_INTENTS.ACCENT} />
        )}
      </Icon>
    </Button>
  )
}

FavoriteDealerButton.displayName = 'FavoriteDealerButton'

FavoriteDealerButton.propTypes = {
  contractId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isFavorite: PropTypes.bool,
  trackingData: PropTypes.object
}

export default FavoriteDealerButton
